import { initialize, isEnabled, Strategy } from "unleash-client";
import { getUnleashSecret } from "util/secret-utils";
import { serverRuntimeConfig } from "util/env-utils";
import { isServer } from "util/browser-utils";

let instance;

const unleashFlags = [
    `unleash-demo`,
    `intercom-enabled`,
    `magnifi-genius-datafeed`,
    `facebook-crossposting`,
    `unleashNewHelpIcon`,
    `unleashServiceWorker`,
    `unleashContentSecurityPolicy`,
    `unleashLiveSourceAudioStreams`,
    `unleashMultiviewerQualitySetting`,
    `unleashProducerAutoRecue`,
    `unleashProducerCompression`,
    `unleashProducerDynamicTransitions`,
    `unleashProducerGuestRemoteControls`,
    `unleashProducerEditLiveSource`,
    `unleashProducerMediaPlayerTrimmer`,
    `unleashProducerQualitySetting`,
    `unleashProducerSupportedBrowsersOnly`,
    `unleashProducerMultiDisplaySettingsEnabled`,
    `unleashProducerMagicLinks`,
    `unleashProducerStereoBalance`,
    `unleashProducerSwitchToPreview`,
    `unleashSocialCredentialsMigration`,
    `unleashProducerVideoAudioDecode`,
    `unleashEditorFifthLayer`,
    `unleashHomeSchedulerToDVR`,
    `unleashProducerMultiTrack`,
    `unleashKeyBindingsActiveTabsIndicator`,
    `unleashKeyboardLocalization`,
    `unleashHideUdvrBroadcasts`,
    `unleashAllowUDVRBroadcasting`,
    `unleashProducerTargetSourceByTitle`,
    `unleashRepeatingXKeysShuttle`,
    `unleashOutputMetrics`,
    `unleashTwitterMonetisationPresets`,
    `unleashThreadsCredential`,
    `unleashStudioShareSidebarMigration`,
    `unleashProducerGatewayDownload`,
    `unleashProducerUDVRMarkers`,
    `unleashProducerThreadsWebsource`,
    `unleashProIngestV2Metrics`,
    `unleashGenAiTitleDesc`,
    `unleashGenAiTitleDescEnableFeature`,
    `unleashMagnifiHighlights`,
    `unleashTrimmerPlaybackSyncMLC`,
    `unleashProducerTextOverlay`,
    `producerOpenCaptions`,
    `unleashStudioBulkStartStop`,
    `unleashYoutubePublishTemplate`,
    `unleashStudioNewStreamSchedulerCalendar`
];

/** Custom Strategies - see https://github.com/Unleash/unleash-client-node#custom-strategies */

/**
 * @summary - organizationWithId custom strategy
 */
class ActiveForCurrentOrganization extends Strategy {
    constructor() {
        super(`organizationWithId`);
    }

    isEnabled(parameters, context) {
        return (
            parameters.organizationIds.indexOf(context.organizationId) !== -1
        );
    }
}

/**
 * @summary - environmentHostname custom strategy. Matches against the hostname the application is currrently deployed to.
 * @example studio.grabyo.com|studio.grbydv.com|localhost
 */
class ActiveForCurrentHostname extends Strategy {
    constructor() {
        super(`environmentHostname`);
    }

    isEnabled(
        parameters: { hostnames: string[] },
        context: { hostname: string }
    ) {
        return parameters.hostnames.indexOf(context.hostname) !== -1;
    }
}

/**
 * @async
 * @summary Initialize Unleash with our application data
 */
export const initUnleash = async () => {
    if (!isServer()) {
        console.warn(
            `This module is intended for server-side use only. You may face unexpected behaviour when using this in the browser.`
        );
    }
    if (!instance) {
        const secret = await getUnleashSecret();

        instance = initialize({
            url: serverRuntimeConfig.UNLEASH.APP_URL,
            appName: serverRuntimeConfig.UNLEASH.APP_NAME,
            customHeaders: { Authorization: secret },
            strategies: [
                new ActiveForCurrentOrganization(),
                new ActiveForCurrentHostname()
            ]
        });
    }
};

/**
 * @summary - Get the status of the specific flag
 * @param feature - Flag name
 * @param context - Context of the request, see Unleash documentation
 */
export const isFeatureEnabled = (feature: string, context = {}) => {
    try {
        return isEnabled(feature, context);
    } catch (e) {
        console.error(
            `Failed to get unleash data for ${feature}. Have you called 'initUnleash()'?`,
            e
        );
        return false;
    }
};

/**
 * @summary - Get the value of all given Unleash flags
 * @param context - Context of the request, see Unleash documentation
 * @param flags - Array of flag names
 */
export const getUnleashFlags = (
    context = {},
    flags?: string[]
): Record<string, boolean> => {
    if (!flags) {
        flags = unleashFlags;
    }

    return flags.reduce(
        (memo, flag) => ({
            ...memo,
            [flag]: isFeatureEnabled(flag, context)
        }),
        {}
    );
};
